import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import CompetitionCard from "@/components/CompetitionCard";
import { Search } from "lucide-react";

export default function Competitions() {
  const [comps, setComps] = useState([]);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("ending");

  useEffect(() => {
    api.get("/competitions").then((r) => setComps(r.data)).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    let list = comps.filter((c) =>
      (c.title + " " + c.subtitle).toLowerCase().includes(q.toLowerCase())
    );
    if (sort === "ending") list = [...list].sort((a, b) => new Date(a.ends_at) - new Date(b.ends_at));
    if (sort === "value") list = [...list].sort((a, b) => b.card_value - a.card_value);
    if (sort === "cheap") list = [...list].sort((a, b) => a.price_per_ticket - b.price_per_ticket);
    return list;
  }, [comps, q, sort]);

  const isClosed = (c) => c.status !== "live" || (c.ends_at && new Date(c.ends_at) <= new Date());
  const live = filtered.filter((c) => !isClosed(c));
  const past = filtered
    .filter((c) => isClosed(c))
    .sort((a, b) => new Date(b.drawn_at || b.ends_at) - new Date(a.drawn_at || a.ends_at));

  return (
    <div className="max-w-7xl mx-auto px-5 md:px-8 py-12 md:py-20" data-testid="competitions-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[color:var(--ufr-green)] font-bold">Live drops</div>
          <h1 className="font-display text-4xl md:text-6xl text-white mt-2">All football raffles</h1>
        </div>
        <div className="flex items-center gap-3 w-full md:w-auto">
          <div className="relative flex-1 md:w-72">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <input
              data-testid="competitions-search"
              className="ufr-input pl-9"
              placeholder="Search by player or set"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <select
            data-testid="competitions-sort"
            className="ufr-input md:w-48"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
          >
            <option value="ending">Ending soonest</option>
            <option value="value">Highest value</option>
            <option value="cheap">Cheapest entry</option>
          </select>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-6">
        <span className="live-dot" />
        <h2 className="font-display text-2xl md:text-3xl text-white" data-testid="live-raffles-heading">Live raffles</h2>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="competitions-grid">
        {live.map((c) => <CompetitionCard key={c.id} c={c} />)}
      </div>
      {live.length === 0 && (
        <div className="text-center text-white/50 py-14 border border-dashed border-white/10 rounded-md" data-testid="competitions-empty">
          No live raffles right now — new drops coming soon.
        </div>
      )}

      {past.length > 0 && (
        <div className="mt-16">
          <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 font-bold">Previously won</div>
          <h2 className="font-display text-2xl md:text-3xl text-white mt-1 mb-6" data-testid="past-raffles-heading">Past raffles</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="past-competitions-grid">
            {past.map((c) => <CompetitionCard key={c.id} c={c} />)}
          </div>
        </div>
      )}
    </div>
  );
}
